import { useState } from "react";
import { Card } from "src/components/card";
import useAccountEnhanced from "../../../hooks/use-account-enhanced";
import dayjs from "dayjs";
import { hrisConfigMutations, hrisConfigQueryOptions } from "src/api/mvte/hris-rep-query-options";
import { useQuery } from "@tanstack/react-query";
import { LastWorkDatePayload } from "../types/LastWorkDatePayload";
import { TerminationReasonPayload } from "../types/TerminationReasonPayload";
import { TerminationCategoryPayload } from "../types/TerminationCategoryPayload";
import { TerminationNotesPayload } from "../types/TerminationNotesPayload";

interface DetailFieldProps
{
    label: string;
    children: React.ReactNode;
}

function DetailField({ label, children }: DetailFieldProps)
{
    return (
        <div>
            <p className="text-sm font-medium text-gray-500">{label}</p>
            <div className="mt-1 text-sm text-gray-900">{children}</div>
        </div>
    );
}

type EditableFieldStatus = { type: "success" | "error"; message: string } | null;

interface EditableDetailFieldProps
{
    label: string;
    isEditing: boolean;
    isSaving: boolean;
    disabled?: boolean;
    saveDisabled?: boolean;
    displayValue: React.ReactNode;
    status?: EditableFieldStatus;
    onEditClick: () => void;
    onCancel: () => void;
    onSave: () => void;
    children: React.ReactNode;
}

function EditableDetailField({
    label,
    isEditing,
    isSaving,
    disabled,
    saveDisabled,
    displayValue,
    status,
    onEditClick,
    onCancel,
    onSave,
    children,
    }: EditableDetailFieldProps)
{
    return (
        <div>
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-500">{label}</p>
                {!isEditing && (
                    <button
                        type="button"
                        onClick={onEditClick}
                        disabled={disabled}
                        className="cursor-pointer text-xs font-medium text-[#0078d4] hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline">
                            Edit
                    </button>
                )}
            </div>

            {isEditing ? (
                <div className="mt-1 flex flex-wrap items-center gap-2">
                    {children}
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={isSaving || saveDisabled}
                        className="cursor-pointer rounded-md bg-[#0078d4] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#0078d4]/90 disabled:cursor-not-allowed disabled:opacity-50">
                            {isSaving ? "Saving..." : "Save"}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSaving}
                        className="cursor-pointer rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                            Cancel
                    </button>
                </div>
            ) : (
                <div className="mt-1 text-sm text-gray-900">{displayValue}</div>
            )}

            {status && (
                <p className={`mt-1 text-xs font-medium ${status.type === "success" ? "text-green-700" : "text-red-700"}`}>
                    {status.message}
                </p>
            )}
        </div>
    );
}
type EditingField = "lastWorkDate" | "reason" | "category" | "notes" | null;

function LastWorkDate()
{
    const accountEnhanced = useAccountEnhanced();

    const lastWorkDateMutation = hrisConfigMutations.useUpdateLastWorkDate();
    const reasonMutation = hrisConfigMutations.useUpdateTerminationReason();
    const categoryMutation = hrisConfigMutations.useUpdateTerminationCategory();
    // TODO: create this mutation in hris-rep-query-options (same pattern as the others)
    const notesMutation = hrisConfigMutations.useUpdateTerminationNotes();

    const [searchId, setSearchId] = useState("");
    const [submittedId, setSubmittedId] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    const [editingField, setEditingField] = useState<EditingField>(null);
    const [draftLastWorkDate, setDraftLastWorkDate] = useState("");
    const [draftReasonId, setDraftReasonId] = useState<number | "">("");
    const [draftCategoryId, setDraftCategoryId] = useState<number | "">("");
    const [draftNotes, setDraftNotes] = useState("");

    const [lastWorkDateStatus, setLastWorkDateStatus] = useState<EditableFieldStatus>(null);
    const [reasonStatus, setReasonStatus] = useState<EditableFieldStatus>(null);
    const [categoryStatus, setCategoryStatus] = useState<EditableFieldStatus>(null);
    const [noteStatus, setNoteStatus] = useState<EditableFieldStatus>(null);

    const parsedId = submittedId ? parseInt(submittedId, 10) : NaN;

    const {
        data: workflowRequest,
        isFetching: isSearching,
        isError,
        isSuccess,
        refetch,
    } = useQuery({
        ...hrisConfigQueryOptions.termWorkflowQueryOptions({ params: { WorkflowRequestLogID: parsedId } }),
        enabled: submittedId !== null && !Number.isNaN(parsedId),
    });

    const employeeType = workflowRequest?.EmployeeType

    const reasonQueryOptions = useQuery({...hrisConfigQueryOptions.termReasonQueryOptions()});
    const { data: reasonOptions } = reasonQueryOptions

    const categoryQueryOptions = useQuery({...hrisConfigQueryOptions.termCategoryQueryOptions({ params: { EmployeeType: employeeType } }),
        enabled: !!employeeType});
    const { data: categoryOptions } = categoryQueryOptions

    const isSearchLocked = !searchId.trim() || isSearching || (isSuccess && searchId.trim() === submittedId)

    const isNotesBlank = draftNotes.trim().length === 0;

    function handleSearch()
    {
        setValidationError(null);
        const trimmed = searchId.trim();

        if (!trimmed)
        {
            setValidationError("Please enter a Workflow Request ID.");
            setSubmittedId(null);
            return;
        }

        if (trimmed === submittedId)
        {
            refetch();
        }
        else
        {
            setSubmittedId(trimmed);
        }
    }

    function closeEdit()
    {
        setEditingField(null);
    }

    function startEditLastWorkDate()
    {
        setDraftLastWorkDate(workflowRequest?.LastWorkDate ?? "");
        setLastWorkDateStatus(null);
        setEditingField("lastWorkDate");
    }

    async function handleSaveLastWorkDate()
    {
        if (!workflowRequest || !draftLastWorkDate) return;

        const payload: LastWorkDatePayload =
        {
            WorkflowRequestLogID: workflowRequest.WorkflowRequestLogID,
            NewLastWorkDate: draftLastWorkDate,
            SourceSystem: "MVTE-Center",
            ChangedByEmployeeCode: accountEnhanced.me.employeeCode,
            Note: "Hardcoded note for now",
        };

        try
        {
            await lastWorkDateMutation.mutateAsync({ payload });
            setLastWorkDateStatus({
                type: "success",
                message: `Updated to ${dayjs(draftLastWorkDate).format("MM/DD/YYYY")}.`,
            });
            closeEdit();
            refetch();
        }
        catch (error)
        {
            setLastWorkDateStatus({ type: "error", message: "Failed to update last work date." });
        }
    }

    function startEditReason()
    {
        const currentReason = reasonOptions?.find(
            (option) => option.ReasonDescription === workflowRequest?.ReasonDescription,
        );
        setDraftReasonId(currentReason?.TerminationReasonID ?? "");
        setReasonStatus(null);
        setEditingField("reason");
    }

    async function handleSaveReason()
    {
        if (!workflowRequest || draftReasonId === "") return;

        const payload: TerminationReasonPayload =
        {
            WorkflowRequestLogID: workflowRequest.WorkflowRequestLogID,
            TerminationReasonID: draftReasonId,
            SourceSystem: "MVTE-Center",
            ChangedByEmployeeCode: accountEnhanced.me.employeeCode,
            Note: "Hardcoded note for now",
        };

        try
        {
            await reasonMutation.mutateAsync({ payload });
            setReasonStatus({ type: "success", message: "Reason updated." });
            closeEdit();
            refetch();
        }
        catch (error)
        {
            setReasonStatus({ type: "error", message: "Failed to update reason." });
        }
    }

    function startEditCategory()
    {
        const currentCategory = categoryOptions?.find(
            (option) => option.CategoryDescription === workflowRequest?.CategoryDescription,
        );
        setDraftCategoryId(currentCategory?.TerminationCategoryID ?? "");
        setCategoryStatus(null);
        setEditingField("category");
    }

    async function handleSaveCategory()
    {
        if (!workflowRequest || draftCategoryId === "") return;

        const payload: TerminationCategoryPayload =
        {
            WorkflowRequestLogID: workflowRequest.WorkflowRequestLogID,
            TerminationCategoryID: draftCategoryId,
            SourceSystem: "MVTE-Center",
            ChangedByEmployeeCode: accountEnhanced.me.employeeCode,
            Note: "Hardcoded note for now",
        };

        try
        {
            await categoryMutation.mutateAsync({ payload });
            setCategoryStatus({ type: "success", message: "Category updated." });
            closeEdit();
            refetch();
        }
        catch (error)
        {
            setCategoryStatus({ type: "error", message: "Failed to update category." });
        }
    }

    function startEditNotes()
    {
        setDraftNotes(workflowRequest?.Notes ?? "");
        setNoteStatus(null);
        setEditingField("notes");
    }

    async function handleSaveNotes()
    {
        if (!workflowRequest || isNotesBlank) return;

        const payload: TerminationNotesPayload =
        {
            WorkflowRequestLogID: workflowRequest.WorkflowRequestLogID,
            TerminationNote: draftNotes.trim(),
            SourceSystem: "MVTE-Center",
            ChangedByEmployeeCode: accountEnhanced.me.employeeCode,
            Note: "Hardcoded note for now"
        };

        try
        {
            await notesMutation.mutateAsync({ payload });
            setNoteStatus({ type: "success", message: "Notes updated." });
            closeEdit();
            refetch();
        }
        catch (error)
        {
            setNoteStatus({ type: "error", message: "Failed to update notes." });
        }
    }

    const searchError =
        validationError ??
        (isError ? "Failed to load workflow request. Please try again." : null) ??
        (submittedId && !isSearching && !workflowRequest ? `No workflow request found for ID "${submittedId}".`: null);

    const isSearchDisabled = !searchId.trim() || isSearching || isSearchLocked;

    return (
        <div className="min-h-screen w-full bg-[#0B1D3A] px-6 py-10 sm:px-10 lg:px-16">
            <div className="w-full">
                <div>
                    <h1 className="text-4xl font-bold text-white">Workflow Request Termination Tool</h1>
                    <p className="mt-2 text-white/80">
                        Search for a workflow request by ID and update the editable information as needed.
                    </p>
                    <hr className="mt-6 border-t-2 border-white/30" />
                </div>

                <Card>
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">{"Search Workflow Request"}</h2>
                        </div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Workflow Request ID</label>
                        <div className="flex gap-3">
                            <input
                                type="number"
                                value={searchId}
                                onChange={(e) => setSearchId(e.target.value)}
                                placeholder="Enter workflow request ID"
                                disabled={isSearching}
                                className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A] disabled:bg-gray-50"/>
                            <button
                                type="button"
                                onClick={handleSearch}
                                disabled={isSearchDisabled}
                                className="flex min-w-[110px] cursor-pointer items-center justify-center gap-2 rounded-md bg-[#0078d4] px-4 py-2 text-sm font-medium text-white hover:bg-[#0078d4]/90 hover:underline disabled:cursor-not-allowed disabled:opacity-70 disabled:no-underline">
                                    {isSearching ? "Searching..." : "Search"}
                            </button>
                        </div>
                        {searchError && (
                            <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                {searchError}
                            </div>
                        )}
                    </div>
                </Card>

                <br />

                <Card>
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">{"Workflow Request Details"}</h2>
                        </div>
                        <p className="mb-4 text-sm text-gray-500">
                            Last Work Date, Reason, Category, and Notes can be edited individually below.
                        </p>
                        <div className="rounded-lg border border-gray-200">
                            {isSearching ? (
                                <div className="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
                                    Loading workflow request...
                                </div>
                            ) : workflowRequest ? (
                                <div className="grid grid-cols-1 gap-6 p-6 sm:grid-cols-3 sm:divide-x sm:divide-gray-200">
                                    <div className="space-y-4 sm:pr-6">
                                        <DetailField label="Workflow Request ID">
                                            {workflowRequest.WorkflowRequestLogID}
                                        </DetailField>
                                        <DetailField label="Employee">
                                            {workflowRequest.FirstName} {workflowRequest.LastName}
                                        </DetailField>
                                        <DetailField label="Employee Code">{workflowRequest.EmployeeCode}</DetailField>

                                        <EditableDetailField
                                            label="Last Work Date"
                                            isEditing={editingField === "lastWorkDate"}
                                            isSaving={lastWorkDateMutation.isPending}
                                            disabled={editingField !== null && editingField !== "lastWorkDate"}
                                            status={lastWorkDateStatus}
                                            displayValue={workflowRequest.LastWorkDate ? workflowRequest.LastWorkDate : "—"}
                                            onEditClick={startEditLastWorkDate}
                                            onCancel={closeEdit}
                                            onSave={handleSaveLastWorkDate}>
                                                <input
                                                    type="date"
                                                    value={draftLastWorkDate}
                                                    onChange={(e) => setDraftLastWorkDate(e.target.value)}
                                                    className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]"/>
                                        </EditableDetailField>
                                    </div>

                                    <div className="space-y-4 sm:px-6">
                                        <DetailField label="Job Title">{workflowRequest.JobTitle}</DetailField>

                                        <EditableDetailField
                                            label="Category"
                                            isEditing={editingField === "category"}
                                            isSaving={categoryMutation.isPending}
                                            disabled={editingField !== null && editingField !== "category"}
                                            status={categoryStatus}
                                            displayValue={
                                                <span className="inline-block rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                                    {workflowRequest.CategoryDescription}
                                                </span>
                                            }
                                            onEditClick={startEditCategory}
                                            onCancel={closeEdit}
                                            onSave={handleSaveCategory}>
                                                <select
                                                    value={draftCategoryId}
                                                    onChange={(e) => setDraftCategoryId(Number(e.target.value))}
                                                    className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
                                                        <option value="" disabled>Select a category</option>
                                                        {categoryOptions?.map((option) => (
                                                            <option key={option.TerminationCategoryID} value={option.TerminationCategoryID}>
                                                                {option.CategoryCode}: {option.CategoryDescription}
                                                            </option>
                                                        ))}
                                                </select>
                                        </EditableDetailField>

                                        <EditableDetailField
                                            label="Reason"
                                            isEditing={editingField === "reason"}
                                            isSaving={reasonMutation.isPending}
                                            disabled={editingField !== null && editingField !== "reason"}
                                            status={reasonStatus}
                                            displayValue={workflowRequest.ReasonDescription}
                                            onEditClick={startEditReason}
                                            onCancel={closeEdit}
                                            onSave={handleSaveReason}>
                                                <select
                                                    value={draftReasonId}
                                                    onChange={(e) => setDraftReasonId(Number(e.target.value))}
                                                    className="rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]">
                                                        <option value="" disabled>Select a reason</option>
                                                        {reasonOptions?.map((option) => (
                                                            <option key={option.TerminationReasonID} value={option.TerminationReasonID}>
                                                                {option.ReasonCode}: {option.ReasonDescription}
                                                            </option>
                                                        ))}
                                                </select>
                                        </EditableDetailField>
                                    </div>

                                    <div className="space-y-4 sm:pl-6">
                                        <EditableDetailField
                                            label="Notes"
                                            isEditing={editingField === "notes"}
                                            isSaving={notesMutation.isPending}
                                            disabled={editingField !== null && editingField !== "notes"}
                                            saveDisabled={isNotesBlank}
                                            status={noteStatus}
                                            displayValue={
                                                <span className="whitespace-pre-wrap break-words">
                                                    {workflowRequest.Notes || "—"}
                                                </span>
                                            }
                                            onEditClick={startEditNotes}
                                            onCancel={closeEdit}
                                            onSave={handleSaveNotes}>
                                                <textarea
                                                    value={draftNotes}
                                                    onChange={(e) => setDraftNotes(e.target.value)}
                                                    rows={5}
                                                    placeholder="Enter notes"
                                                    aria-invalid={isNotesBlank}
                                                    className="w-full resize-y rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-900 placeholder-gray-400 focus:border-[#0B1D3A] focus:outline-none focus:ring-1 focus:ring-[#0B1D3A]"/>
                                                {isNotesBlank && (
                                                    <p className="w-full text-xs text-gray-500">Notes can't be blank.</p>
                                                )}
                                        </EditableDetailField>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-10 text-center text-sm text-gray-400">
                                    Search for a workflow request above to view its details.
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}
export { LastWorkDate };